/**
 * HubChannelGroups - Main Hub view with channel groups
 *
 * Displays communication channels in a grouped layout similar to TodayView.
 * Each channel shows the latest items, not just counts.
 */

import { useMemo, useState, useEffect } from 'react';
import {
  Mail,
  Phone,
  Video,
  MessageCircle,
  Send,
  Users,
  ChevronDown,
  ChevronRight,
  Inbox,
} from 'lucide-react';
import { useHubStore } from '@business-assistant/shared';
import { listUnseenMessages, type ImapMessage } from '@business-assistant/shared';
import { renderEntityRow } from '../common/registry/ComponentRouter';
import type { Email } from '../rows/EmailRow';

// =============================================================================
// TYPES
// =============================================================================

export type ChannelType = 'email' | 'calls' | 'videocall' | 'messages' | 'whatsapp' | 'telegram' | 'links' | 'contacts';

interface ChannelSubgroup {
  id: string;
  label: string;
  icon?: React.ReactNode;
  color: string;
  entities: any[];
  type: string;
}

interface ChannelModule {
  moduleId: ChannelType;
  moduleName: string;
  icon: React.ReactNode;
  color: string;
  subgroups: ChannelSubgroup[];
}

interface HubChannelGroupsProps {
  onSelectChannel: (channel: ChannelType, subgroup?: string) => void;
  emailStats?: { inbox: number; sent: number };
  onAvatarClick?: (name: string, id?: string) => void;
  /** Called when clicking on an entity (email, conversation, contact) */
  onEntityClick?: (entityId: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function HubChannelGroups({ onSelectChannel, emailStats, onAvatarClick, onEntityClick }: HubChannelGroupsProps) {
  const { conversations, contacts } = useHubStore();
  const [recentEmails, setRecentEmails] = useState<ImapMessage[]>([]);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set(['email', 'whatsapp', 'telegram', 'calls', 'videocall']));

  // Load unread emails from IMAP
  useEffect(() => {
    const loadEmails = async () => {
      try {
        // Fetch ALL unread emails (up to 100), not just 5 recent
        const messages = await listUnseenMessages('INBOX', 100);
        setRecentEmails(messages);
      } catch {
        // IMAP not connected
      }
    };
    loadEmails();
  }, []);

  // Convert IMAP message to standardized Email format
  const imapToEmail = (msg: ImapMessage): Email => ({
    id: `eml-${msg.uid}`,
    from: msg.from ? (msg.from.name ? `${msg.from.name} <${msg.from.address}>` : msg.from.address) : undefined,
    to: msg.to.length > 0 ? (msg.to[0].name ? `${msg.to[0].name} <${msg.to[0].address}>` : msg.to[0].address) : undefined,
    subject: msg.subject || '(No subject)',
    body: msg.snippet,
    direction: 'in' as const,
    at: msg.date,
    read: msg.flags.includes('\\Seen'),
    hasAttachments: msg.hasAttachments,
  });

  // Build module groups with entities
  const moduleGroups = useMemo(() => {
    const groups: ChannelModule[] = [];

    // =========================================================================
    // EMAIL Module
    // =========================================================================
    const emailModule: ChannelModule = {
      moduleId: 'email',
      moduleName: 'EMAIL',
      icon: <Mail size={20} />,
      color: '#3b82f6',
      subgroups: [],
    };

    // Unread inbox emails from IMAP - show ALL unread, not just 5
    if (recentEmails.length > 0) {
      emailModule.subgroups.push({
        id: 'inbox',
        label: `Unread (${recentEmails.length})`,
        icon: <Inbox size={16} />,
        color: '#3b82f6',
        // Convert IMAP messages to standard Email format for renderEntityRow
        entities: recentEmails.map(imapToEmail),
        type: 'emails',
      });
    }

    // Email conversations from store (fallback when IMAP not connected)
    const emailConvs = conversations.filter(c => c.channel === 'email' && (c.unread || 0) > 0);
    if (emailConvs.length > 0 && recentEmails.length === 0) {
      emailModule.subgroups.push({
        id: 'inbox',
        label: `Unread (${emailConvs.length})`,
        icon: <Inbox size={16} />,
        color: '#3b82f6',
        entities: emailConvs,
        type: 'conversations',
      });
    }

    if (emailModule.subgroups.length > 0) {
      groups.push(emailModule);
    }

    // =========================================================================
    // WHATSAPP Module
    // =========================================================================
    const waConvs = conversations.filter(c => c.channel === 'whatsapp');
    if (waConvs.length > 0) {
      const waModule: ChannelModule = {
        moduleId: 'whatsapp',
        moduleName: 'WHATSAPP',
        icon: <MessageCircle size={20} />,
        color: '#22c55e',
        subgroups: [{
          id: 'all',
          label: 'Recent messages',
          icon: <MessageCircle size={16} />,
          color: '#22c55e',
          entities: waConvs.slice(0, 5),
          type: 'conversations',
        }],
      };
      groups.push(waModule);
    }

    // =========================================================================
    // TELEGRAM Module
    // =========================================================================
    const tgConvs = conversations.filter(c => c.channel === 'telegram');
    if (tgConvs.length > 0) {
      const tgModule: ChannelModule = {
        moduleId: 'telegram',
        moduleName: 'TELEGRAM',
        icon: <Send size={20} />,
        color: '#0088cc',
        subgroups: [{
          id: 'all',
          label: 'Recent messages',
          icon: <Send size={16} />,
          color: '#0088cc',
          entities: tgConvs.slice(0, 5),
          type: 'conversations',
        }],
      };
      groups.push(tgModule);
    }

    // =========================================================================
    // CALLS Module
    // =========================================================================
    const callConvs = conversations.filter(c => c.channel === 'call' || c.channel === 'videocall');
    if (callConvs.length > 0) {
      const callsModule: ChannelModule = {
        moduleId: 'calls',
        moduleName: 'CALLS',
        icon: <Phone size={20} />,
        color: '#f59e0b',
        subgroups: [{
          id: 'all',
          label: 'History',
          icon: <Phone size={16} />,
          color: '#f59e0b',
          entities: callConvs.slice(0, 5),
          type: 'conversations',
        }],
      };
      groups.push(callsModule);
    }

    // =========================================================================
    // VIDEOCALL Module (dedicated, separate from calls)
    // =========================================================================
    const videocallConvs = conversations.filter(c => c.channel === 'videocall');
    if (videocallConvs.length > 0) {
      const videocallModule: ChannelModule = {
        moduleId: 'videocall',
        moduleName: 'VIDEO CALLS',
        icon: <Video size={20} />,
        color: '#7C3AED',
        subgroups: [{
          id: 'all',
          label: 'Meetings',
          icon: <Video size={16} />,
          color: '#7C3AED',
          entities: videocallConvs.slice(0, 5),
          type: 'conversations',
        }],
      };
      groups.push(videocallModule);
    }

    // =========================================================================
    // CONTACTS Module
    // =========================================================================
    if (contacts.length > 0) {
      const contactsModule: ChannelModule = {
        moduleId: 'contacts',
        moduleName: 'CONTACTS',
        icon: <Users size={20} />,
        color: '#10b981',
        subgroups: [{
          id: 'all',
          label: 'Address Book',
          icon: <Users size={16} />,
          color: '#10b981',
          entities: contacts.slice(0, 5),
          type: 'contacts',
        }],
      };
      groups.push(contactsModule);
    }

    return groups;
  }, [conversations, contacts, recentEmails]);

  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  return (
    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {moduleGroups.map((module) => {
        const isExpanded = expandedModules.has(module.moduleId);
        const totalCount = module.subgroups.reduce((sum, sg) => sum + sg.entities.length, 0);

        return (
          <section key={module.moduleId}>
            {/* Module header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem',
                paddingBottom: '0.75rem',
                borderBottom: '2px solid #e5e7eb',
                cursor: 'pointer',
              }}
              onClick={() => toggleModule(module.moduleId)}
            >
              <span style={{ color: module.color, display: 'flex' }}>
                {module.icon}
              </span>
              <h2 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 700,
                color: module.color,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}>
                {module.moduleName}
              </h2>
              <span style={{ fontSize: '1rem', color: '#9ca3af', fontWeight: 500 }}>
                ({totalCount})
              </span>
              <div style={{ flex: 1 }} />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectChannel(module.moduleId);
                }}
                style={{
                  padding: '0.375rem 0.75rem',
                  background: `${module.color}15`,
                  color: module.color,
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                View all
              </button>
              {isExpanded ? (
                <ChevronDown size={20} style={{ color: '#9ca3af' }} />
              ) : (
                <ChevronRight size={20} style={{ color: '#9ca3af' }} />
              )}
            </div>

            {/* Subgroups with entities */}
            {isExpanded && module.subgroups.map((subgroup, idx) => (
              <div key={subgroup.id} style={{ marginBottom: '1.5rem' }}>
                {/* Subgroup header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => onSelectChannel(module.moduleId, subgroup.id)}
                >
                  <span style={{ color: subgroup.color, display: 'flex' }}>
                    {subgroup.icon}
                  </span>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: '#374151',
                  }}>
                    {subgroup.label}
                  </h3>
                  <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    ({subgroup.entities.length})
                  </span>
                </div>

                {/* Entity rows - ALL use renderEntityRow for consistency */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {subgroup.entities.map((entity: any) =>
                    renderEntityRow(entity, {
                      onTitleClick: () => onEntityClick?.(entity.id),
                      onAvatarClick,
                    })
                  )}
                </div>
              </div>
            ))}
          </section>
        );
      })}

      {moduleGroups.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '3rem' }}>
          <MessageCircle size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <p>No recent communications</p>
        </div>
      )}
    </div>
  );
}

export default HubChannelGroups;
